import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { extractPlainTextFromHtml } from '@vmos2/utils';
import type { NutritionType, Diet, Allergen, Ingredients } from 'types';

import { Tab, TabList, TabPanel, Tabs } from '../../tabs';
import { Nutrition } from '../nutrition/Nutrition';
import { AllergensDiets } from '../AllergensDiets';
import { useComponentTheme } from '../../useComponentTheme';
import { Macros } from './macros';

type Props = {
  calories: ?number,
  nutrition?: NutritionType,
  allergens?: Allergen[],
  diets?: Diet[],
  mode?: String,
  allergensDisplayMode?: 'icon' | 'text',
  dietsDisplayMode?: 'icon' | 'text',
  ingredients?: Ingredients[] | boolean,
  nutritionMode: 'general' | 'dietary',
  component: string,
  isLarge: boolean,
  isPrepackaged?: boolean,
  measurementUnits?: {
    symbol: string,
  },
};

export const MealTabs = ({
  nutrition,
  calories,
  allergens,
  diets,
  mode,
  component,
  allergensDisplayMode,
  dietsDisplayMode,
  ingredients,
  nutritionMode,
  isLarge,
  isPrepackaged,
  measurementUnits,
}: Props) => {
  const { t } = useTranslation('bundleCustomization.sidebar');
  const componentTheme = useComponentTheme(theme => theme.components[component], mode);

  const isGeneralNutritionMode = nutritionMode === 'general';
  const schemeMode = isLarge ? componentTheme.mode : 'light';
  const translationParams = { context: isPrepackaged ? 'prepackaged' : undefined, units: measurementUnits?.symbol };
  const shouldShowIngredientsTab = Array.isArray(ingredients);
  const energySummaryTitle = [
    t('energyValueSummary'),
    isGeneralNutritionMode && t('nutrition:macros'),
    allergens.length > 0 && t('dietAllergies:allergensTitle'),
  ]
    .filter(Boolean)
    .join(' & ');

  return (
    <Tabs schemeMode={schemeMode} mode="Meal" type="card">
      <TabList scrollToActive={false}>
        <Tab data-test={`tab-${t('tab1')}`} id="meal-tab-1" aria-controls="tab-panel-macros">
          {isGeneralNutritionMode ? t('tab1', translationParams) : t('dietaryTab', translationParams)}
        </Tab>
        {shouldShowIngredientsTab && (
          <Tab data-test={`tab-${t('tab3')}`} id="meal-tab-3" aria-controls="tab-panel-ingredients">
            {t('tab3', translationParams)}
          </Tab>
        )}
        <Tab data-test={`tab-${t('tab2')}`} id="meal-tab-2" aria-controls="tab-panel-nutrition">
          {t('tab2', translationParams)}
        </Tab>
      </TabList>
      <TabPanel data-test="tab-panel-macros" id="tab-panel-macros" accessibilityTitle={energySummaryTitle}>
        {isGeneralNutritionMode && <Macros calories={calories} nutrition={nutrition} />}
        <AllergensDiets
          allergens={allergens}
          diets={diets}
          isLarge={isLarge}
          component={component}
          allergensDisplayMode={allergensDisplayMode}
          dietsDisplayMode={dietsDisplayMode}
          calories={isPrepackaged ? null : calories}
          mode={schemeMode}
        />
      </TabPanel>
      {shouldShowIngredientsTab && (
        <TabPanel
          data-test="tab-panel-ingredients"
          id="tab-panel-ingredients"
          accessibilityTitle={t('ingredientsSummary')}
        >
          {ingredients.map(({ uuid, name, ingredients }) => (
            <Ingredient key={uuid}>
              {name && <IngredientTitle>{name}</IngredientTitle>}
              <div aria-hidden dangerouslySetInnerHTML={{ __html: ingredients }} />
              <p className="hide-text-show-focus">{extractPlainTextFromHtml(ingredients)}</p>
            </Ingredient>
          ))}
        </TabPanel>
      )}
      <TabPanel data-test="tab-panel-nutrition" id="tab-panel-nutrition" accessibilityTitle={t('nutritionFacts')}>
        <Nutrition calories={calories} nutrition={nutrition} />
      </TabPanel>
    </Tabs>
  );
};

const Ingredient = styled('div')`
  position: relative;
  margin: 0 0 20px;
`;

const IngredientTitle = styled('div')`
  text-decoration: underline;
  font-weight: bold;
`;

MealTabs.defaultProps = {
  styleOptions: {},
  ingredients: null,
  nutritionMode: 'general',
};
